<template>
  <div
    class="bg-surface-default-low rounded-lg flex flex-col gap-16 px-16 py-16"
  >
    <RevTag
      class="self-start"
      :label="i18n(translations.bestTimeToSell)"
      variant="success"
    />

    <div class="relative flex flex-col">
      <span
        class="text-static-default-mid text-caption font-weight-caption-bold ml-18 md:mb-10"
      >
        {{ i18n(translations.now) }}
      </span>

      <img
        alt="Trade-in value graph"
        class="mb-16 w-full px-18"
        src="/img/buyback/offer-graph.svg"
      />

      <div class="mx-18 flex justify-between">
        <div ref="offer-graph-start-price" class="flex flex-col">
          <div class="bg-static-brand-mid rounded-sm p-4">
            <span class="text-static-brand-mid caption md:body-2">
              {{
                i18n.price(price, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })
              }}
            </span>
          </div>
        </div>

        <div class="caption flex flex-col gap-2 md:body-2">
          <span class="text-static-brand-mid">
            {{
              getPriceEstimate(
                i18n.price(oneMonthPrice, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }),
              )
            }}
          </span>
          <span class="text-static-default-low">
            {{ i18n(translations.oneMonth) }}
          </span>
        </div>

        <div class="caption flex flex-col gap-2 md:body-2">
          <span class="text-static-brand-mid">
            {{
              getPriceEstimate(
                i18n.price(threeMonthsPrice, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }),
              )
            }}
          </span>
          <span class="text-static-default-low">
            {{ i18n(translations.threeMonths) }}
          </span>
        </div>

        <div class="caption flex flex-col gap-2 md:body-2">
          <span class="text-static-brand-mid">
            {{
              getPriceEstimate(
                i18n.price(sixMonthsPrice, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }),
              )
            }}
          </span>
          <span class="text-static-default-low">
            {{ i18n(translations.sixMonths) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Device name and disclaimer -->
    <div class="text-center">
      <p class="text-static-default-mid body-2-bold md:body-1-bold">
        {{ i18n(translations.tradeInValue, { product: deviceName }) }}
      </p>
      <p class="text-static-default-low caption md:body-2">
        {{ i18n(translations.disclaimer) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTag } from '@ds/components/Tag'

import translations from './OfferGraph.translations'

const props = defineProps<{
  price: Price
  deviceName: string
  tiering: 'low' | 'medium' | 'high' | 'great'
}>()

const i18n = useI18n()

const decreaseFactor = computed(() => {
  switch (props.tiering) {
    case 'great':
      return 0.97

    case 'high':
      return 0.98

    case 'low':
    case 'medium':
    default:
      return 0.99
  }
})

const oneMonthPrice = computed<Price>(() => {
  const amount = parseFloat(props.price.amount)
  const decreased = amount * decreaseFactor.value

  return {
    amount: decreased.toFixed(2),
    currency: props.price.currency,
  }
})

const threeMonthsPrice = computed<Price>(() => {
  const amount = parseFloat(props.price.amount)
  const decreased = amount * decreaseFactor.value ** 3

  return {
    amount: decreased.toFixed(2),
    currency: props.price.currency,
  }
})

const sixMonthsPrice = computed<Price>(() => {
  const amount = parseFloat(props.price.amount)
  const decreased = amount * decreaseFactor.value ** 6

  return {
    amount: decreased.toFixed(2),
    currency: props.price.currency,
  }
})

const getPriceEstimate = (price: string) => {
  return `${price}*`
}
</script>
